import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Copy,
  LayoutTemplate,
  Loader2,
  Palette,
  Pencil,
  Plus,
  Save,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { equipmentAPI } from "../../../../services/equipmentAPI";

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:bg-gray-50 disabled:text-gray-500";

const COLOR_PALETTE = [
  "#FCE5CD",
  "#F4CCCC",
  "#D9EAD3",
  "#CFE2F3",
  "#D9D2E9",
  "#FFF2CC",
  "#D0E0E3",
  "#EAD1DC",
  "#E5E7EB",
  "#FFFFFF",
];

const FIELD_TYPES = [
  ["select", "Dropdown — designer-defined options"],
  ["radio", "Radio choices — designer-defined options"],
  ["checkbox", "Tick box"],
  ["text", "Short text"],
  ["textarea", "Long text / comment"],
  ["number", "Number"],
  ["attachment", "Document Upload / Attachment"],
  ["equipment_field", "Equipment information — auto-filled"],
];

const RESPONSIBILITY_GROUPS = [
  ["any", "Any project member"],
  ["pe", "PE"],
  ["qaqc", "QA/QC"],
  ["field", "Field"],
  ["third_party", "3rd Party"],
];

const EQUIPMENT_FIELDS = [
  ["equipment_number", "Equipment Number"],
  ["equipment_tag", "Equipment Tag / Callout"],
  ["equipment_classification_name", "Equipment Classification"],
  ["equipment_specific_type_name", "Equipment Type"],
  // Legacy source key retained for older templates; it resolves to Classification.
  ["equipment_type_name", "Equipment Classification (legacy)"],
  ["room_name", "Location / Room"],
  ["skid_name", "Skid"],
  ["manufacturer", "Manufacturer"],
  ["model_number", "Model Number"],
  ["serial_number", "Serial Number"],
  ["specification_number", "Specification #"],
  ["callout", "Equipment Callout"],
];

const apiMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return error?.message || fallback;
};


const safeColor = (value, fallback = "#E5E7EB") =>
  /^#[0-9A-Fa-f]{6}$/.test(String(value || "")) ? value : fallback;

const tint = (hex, alpha = 0.13) => {
  const clean = safeColor(hex).slice(1);
  const red = parseInt(clean.slice(0, 2), 16);
  const green = parseInt(clean.slice(2, 4), 16);
  const blue = parseInt(clean.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const layeredToneStyle = (hex, { from = 0.18, to = 0.08, borderAlpha = 0.34 } = {}) => ({
  background: `linear-gradient(180deg, ${tint(hex, from)} 0%, ${tint(hex, to)} 100%)`,
  borderColor: tint(hex, borderAlpha),
});

const uid = (prefix) =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const defaultStages = () => [
  { id: "stage_incomplete", label: "Document Review and Arrival", color: "#FCE5CD", legacy_status: "incomplete", sort_order: 0, is_active: true },
  { id: "stage_assembled", label: "Assembled / Ready for Delivery", color: "#F4CCCC", legacy_status: "assembled", sort_order: 1, is_active: true },
  { id: "stage_installed", label: "Installed", color: "#D9EAD3", legacy_status: "installed", sort_order: 2, is_active: true },
  { id: "stage_pre_energization", label: "Pre-Energization Complete", color: "#CFE2F3", legacy_status: "pre_energization", sort_order: 3, is_active: true },
  { id: "stage_energized_commissioned", label: "Energized & Commissioned", color: "#D9D2E9", legacy_status: "energized_commissioned", sort_order: 4, is_active: true },
];

const defaultLegend = () => [
  { id: "legend_qaqc", label: "Office / QAQC", color: "#FCE5CD", description: "Office or QA/QC input", sort_order: 0 },
  { id: "legend_field", label: "Field", color: "#D9EAD3", description: "Field input", sort_order: 1 },
  { id: "legend_third_party", label: "3rd Party", color: "#D9D2E9", description: "Third-party input", sort_order: 2 },
  { id: "legend_pe", label: "PE", color: "#CFE2F3", description: "Project Engineer input", sort_order: 3 },
];

const defaultDefinition = (name) => ({
  schema_version: 5,
  layout_engine: "structured",
  title: name || "Equipment Checklist",
  stages: defaultStages(),
  color_legend: defaultLegend(),
  report_layout: {
    style: "pfc_table",
    show_color_legend: true,
    include_answered_by: true,
    include_answered_at: true,
    include_comments: true,
  },
  sections: [],
});

const normalizeClientDefinition = (incoming, name) => {
  if (incoming?.layout_engine === "structured" && Array.isArray(incoming?.stages)) {
    const next = { ...incoming, sections: [...(incoming.sections || [])] };
    next.stages = (next.stages || []).map((stage) => {
      const label = String(stage?.label || "").trim().toLowerCase();
      return stage?.legacy_status === "incomplete" &&
        ["documentation", "incomplete", "document review and arrival"].includes(label)
        ? { ...stage, label: "Document Review and Arrival" }
        : stage;
    });
    return next;
  }
  return defaultDefinition(name);
};

const StatusChip = ({ status }) => {
  const classes =
    status === "published"
      ? "bg-green-100 text-green-800"
      : status === "draft"
        ? "bg-amber-100 text-amber-800"
        : "bg-gray-100 text-gray-600";
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${classes}`}>
      {status}
    </span>
  );
};

const ColorPicker = ({ value, onChange, disabled }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {COLOR_PALETTE.map((color) => (
      <button
        key={color}
        type="button"
        disabled={disabled}
        title={color}
        onClick={() => onChange(color)}
        className={`h-7 w-7 rounded-md border-2 ${
          value === color
            ? "border-gray-950 ring-2 ring-gray-300"
            : "border-white ring-1 ring-gray-300"
        } disabled:opacity-50`}
        style={{ backgroundColor: color }}
      />
    ))}
    <input
      type="color"
      disabled={disabled}
      value={value || "#E5E7EB"}
      onChange={(event) => onChange(event.target.value.toUpperCase())}
      className="h-8 w-10 rounded border bg-white p-0.5 disabled:opacity-50"
      title="Custom color"
    />
  </div>
);

const moveItem = (items, id, direction) => {
  const next = [...items];
  const index = next.findIndex((item) => item.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= next.length) return items;
  [next[index], next[target]] = [next[target], next[index]];
  return next.map((item, order) => ({ ...item, sort_order: order }));
};

function StructuredTemplateEditor({ projectId, versionId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [definition, setDefinition] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [busy, setBusy] = useState("load");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [configTab, setConfigTab] = useState("blocks");

  const load = useCallback(async () => {
    setBusy("load");
    setError("");
    try {
      const response = await equipmentAPI.getChecklistTemplateVersion(projectId, versionId);
      setData(response.data);
      const next = normalizeClientDefinition(
        response.data?.definition,
        response.data?.template?.name,
      );
      setDefinition(next);
      setSelectedSectionId((current) =>
        current && next.sections?.some((section) => section.id === current)
          ? current
          : next.sections?.[0]?.id || null,
      );
    } catch (err) {
      setError(apiMessage(err, "Failed to load Equipment checklist template."));
    } finally {
      setBusy("");
    }
  }, [projectId, versionId]);

  useEffect(() => {
    load();
  }, [load]);

  const readOnly = data?.status !== "draft";
  const sections = useMemo(() => definition?.sections || [], [definition]);
  const checklistSections = useMemo(
    () => sections.filter((section) => section.section_kind !== "equipment_info"),
    [sections],
  );
  const activeStages = useMemo(
    () => (definition?.stages || []).filter((stage) => stage.is_active !== false),
    [definition],
  );
  const stageById = useMemo(
    () => Object.fromEntries((definition?.stages || []).map((stage) => [stage.id, stage])),
    [definition],
  );
  const legendById = useMemo(
    () => Object.fromEntries((definition?.color_legend || []).map((item) => [item.id, item])),
    [definition],
  );
  const selectedSection = useMemo(
    () => sections.find((section) => section.id === selectedSectionId) || null,
    [sections, selectedSectionId],
  );
  const selectedIsOther = selectedSection?.section_kind === "equipment_info";

  const patchSection = (sectionId, patch) => {
    setDefinition((current) => ({
      ...current,
      sections: current.sections.map((section) =>
        section.id === sectionId ? { ...section, ...patch } : section,
      ),
    }));
  };

  const patchQuestion = (sectionId, questionId, patch) => {
    setDefinition((current) => ({
      ...current,
      sections: current.sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              questions: (section.questions || []).map((question) =>
                question.id === questionId ? { ...question, ...patch } : question,
              ),
            }
          : section,
      ),
    }));
  };

  const patchField = (sectionId, questionId, fieldId, patch) => {
    setDefinition((current) => ({
      ...current,
      sections: current.sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              questions: (section.questions || []).map((question) =>
                question.id === questionId
                  ? {
                      ...question,
                      fields: (question.fields || []).map((field) =>
                        field.id === fieldId ? { ...field, ...patch } : field,
                      ),
                    }
                  : question,
              ),
            }
          : section,
      ),
    }));
  };

  const addLegend = () => {
    const item = {
      id: uid("legend"),
      label: "New Legend",
      color: "#E5E7EB",
      description: "",
      sort_order: definition.color_legend.length,
    };
    setDefinition((current) => ({
      ...current,
      color_legend: [...(current.color_legend || []), item],
    }));
  };

  const removeLegend = (legendId) => {
    if (!window.confirm("Delete this color legend entry? Blocks/questions using it will keep their current color.")) return;
    setDefinition((current) => ({
      ...current,
      color_legend: (current.color_legend || []).filter((item) => item.id !== legendId),
      sections: current.sections.map((section) => ({
        ...section,
        legend_id: section.legend_id === legendId ? null : section.legend_id,
        questions: (section.questions || []).map((question) => ({
          ...question,
          legend_id: question.legend_id === legendId ? null : question.legend_id,
        })),
      })),
    }));
  };

  const addBlock = (kind) => {
    const stage = activeStages[0];
    if (!stage) return;
    const isOther = kind === "equipment_info";
    const section = {
      id: uid(isOther ? "other" : "part"),
      title: isOther
        ? "OTHER — EQUIPMENT INFORMATION"
        : `PART ${checklistSections.length + 1}. NEW PART`,
      stage_id: stage.id,
      lifecycle_stage: stage.legacy_status,
      section_kind: isOther ? "equipment_info" : "checklist",
      equipment_status_mapping: isOther ? null : stage.legacy_status,
      required: !isOther,
      legend_id: null,
      color: isOther ? "#FFFFFF" : stage.color,
      description: isOther
        ? "Stage-linked information block. Set Equipment status mapping to the selected stage if these fields must count toward lifecycle completion; leave it None for informational-only content."
        : "",
      questions: [],
      sort_order: sections.length,
    };
    setDefinition((current) => ({
      ...current,
      sections: [...(current.sections || []), section],
    }));
    setSelectedSectionId(section.id);
    setConfigTab("blocks");
  };

  const changeBlockKind = (nextKind) => {
    if (!selectedSection) return;
    const isOther = nextKind === "equipment_info";
    const stage = stageById[selectedSection.stage_id] || activeStages[0];
    patchSection(selectedSection.id, {
      section_kind: nextKind,
      required: isOther ? false : true,
      stage_id: stage?.id || activeStages[0]?.id,
      lifecycle_stage: stage?.legacy_status || "incomplete",
      equipment_status_mapping: isOther ? null : stage?.legacy_status || "incomplete",
      questions: (selectedSection.questions || []).map((question) => ({
        ...question,
        required: isOther ? false : question.required !== false,
        fields: (question.fields || []).map((field) => ({
          ...field,
          required: isOther ? false : field.required,
        })),
      })),
    });
  };

  const removeSection = (sectionId) => {
    const section = sections.find((item) => item.id === sectionId);
    const label = section?.section_kind === "equipment_info" ? "Other block" : "Part";
    if (!window.confirm(`Remove this ${label} from the template? Existing responses remain archived on active equipment after publish.`)) return;
    const remaining = sections.filter((item) => item.id !== sectionId);
    setDefinition((current) => ({
      ...current,
      sections: remaining.map((item, index) => ({ ...item, sort_order: index })),
    }));
    setSelectedSectionId((current) =>
      current === sectionId ? remaining[0]?.id || null : current,
    );
  };

  const moveSection = (direction) => {
    if (!selectedSection) return;
    setDefinition((current) => ({
      ...current,
      sections: moveItem(current.sections || [], selectedSection.id, direction),
    }));
  };

  const addQuestion = () => {
    if (!selectedSection) return;
    const isOther = selectedSection.section_kind === "equipment_info";
    const otherCounts = Boolean(selectedSection.equipment_status_mapping);
    const question = {
      id: uid("q"),
      label: isOther ? "New information item" : "New checklist question",
      required: !isOther || otherCounts,
      responsibility_group: "any",
      legend_id: selectedSection.legend_id || null,
      color: selectedSection.color,
      help_text: "",
      sort_order: (selectedSection.questions || []).length,
      fields: [],
    };
    patchSection(selectedSection.id, {
      questions: [...(selectedSection.questions || []), question],
    });
  };

  const addField = (questionId, type = "select") => {
    if (!selectedSection) return;
    const question = (selectedSection.questions || []).find((item) => item.id === questionId);
    if (!question) return;
    const field = {
      id: uid("field"),
      label:
        type === "attachment"
          ? "Document Upload"
          : type === "textarea"
            ? "Comment"
            : type === "equipment_field"
              ? "Equipment value"
              : "Response",
      type,
      required: false,
      conditional_required: null,
      options: [],
      failure_values: [],
      source_key: type === "equipment_field" ? "equipment_tag" : null,
      placeholder: "",
      help_text: "",
      require_accepted: false,
      sort_order: (question.fields || []).length,
    };
    patchQuestion(selectedSection.id, questionId, {
      fields: [...(question.fields || []), field],
    });
  };

  const removeQuestion = (questionId) => {
    if (!selectedSection) return;
    if (!window.confirm("Remove this item? Existing responses are archived after publish.")) return;
    patchSection(selectedSection.id, {
      questions: (selectedSection.questions || []).filter((question) => question.id !== questionId),
    });
  };

  const removeField = (questionId, fieldId) => {
    if (!selectedSection) return;
    if (!window.confirm("Remove this response field? Existing historical values remain in audit/report history.")) return;
    const question = (selectedSection.questions || []).find((item) => item.id === questionId);
    patchQuestion(selectedSection.id, questionId, {
      fields: (question?.fields || []).filter((field) => field.id !== fieldId),
    });
  };

  const save = async () => {
    setBusy("save");
    setError("");
    setMessage("");
    try {
      const response = await equipmentAPI.updateChecklistTemplateVersion(projectId, versionId, {
        definition,
        change_note: "Flexible Equipment checklist updated",
      });
      setData(response.data);
      setDefinition(normalizeClientDefinition(response.data.definition, response.data?.template?.name));
      setMessage("Template saved. Publish when the revision is ready for active equipment.");
      await onSaved?.();
      return response.data;
    } catch (err) {
      setError(apiMessage(err, "Failed to save Equipment checklist template."));
      return null;
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    setBusy("publish");
    setError("");
    setMessage("");
    try {
      await equipmentAPI.updateChecklistTemplateVersion(projectId, versionId, {
        definition,
        change_note: "Flexible Equipment checklist ready to publish",
      });
      const response = await equipmentAPI.publishChecklistTemplateVersion(projectId, versionId, {
        change_note: "Published flexible Equipment checklist",
      });
      setData(response.data);
      setDefinition(normalizeClientDefinition(response.data.definition, response.data?.template?.name));
      const synced = Number(response.data?.synced_active_checklists || 0);
      const autoAssigned = Number(response.data?.auto_assignment?.assigned || 0);
      const publishMessages = ["Published."];
      if (synced) {
        publishMessages.push(`${synced} active equipment checklist${synced === 1 ? " was" : "s were"} synchronized without overwriting responses.`);
      }
      if (autoAssigned) {
        publishMessages.push(`${autoAssigned} matching equipment record${autoAssigned === 1 ? " received" : "s received"} this default checklist automatically.`);
      } else if (!synced) {
        publishMessages.push("New equipment can now use this template.");
      }
      setMessage(publishMessages.join(" "));
      await onSaved?.();
    } catch (err) {
      setError(apiMessage(err, "Failed to publish Equipment checklist template."));
    } finally {
      setBusy("");
    }
  };

  if (busy === "load" || !definition) {
    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center bg-gray-950/50">
        <div className="rounded-2xl bg-white p-8">
          <Loader2 className="h-7 w-7 animate-spin text-orange-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-slate-100">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b bg-white px-5 py-3 shadow-sm">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <LayoutTemplate className="h-5 w-5 text-orange-600" />
            <h2 className="text-lg font-bold text-gray-950">{data?.template?.name}</h2>
            <StatusChip status={data?.status} />
            <span className="text-xs text-gray-500">Version {data?.version_number}</span>
          </div>
          <p className="mt-0.5 text-sm text-gray-500">
            Build one ordered form from Parts and Other blocks. Assign each block to the fixed Equipment lifecycle, configure color legends, questions/items and designer-defined response fields. User/date-time are recorded automatically.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!readOnly && (
            <button type="button" onClick={save} disabled={Boolean(busy)} className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold disabled:opacity-50">
              {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          )}
          {!readOnly && (
            <button type="button" onClick={publish} disabled={Boolean(busy)} className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Publish & sync
            </button>
          )}
          <button type="button" onClick={onClose} className="rounded-xl border bg-white p-2.5"><X className="h-5 w-5" /></button>
        </div>
      </header>

      {error && <div className="border-b border-red-200 bg-red-50 px-5 py-2 text-sm text-red-700">{error}</div>}
      {message && <div className="border-b border-green-200 bg-green-50 px-5 py-2 text-sm text-green-800">{message}</div>}
      {readOnly && <div className="border-b border-blue-200 bg-blue-50 px-5 py-2 text-sm text-blue-800">Published versions are read-only. Open the template from the list to create the next revision.</div>}

      <div className="flex gap-2 border-b bg-white px-4 py-2">
        {[
          ["blocks", "Parts & Other"],
          ["legend", "Color Legend"],
        ].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setConfigTab(id)} className={`rounded-lg px-3 py-2 text-sm font-semibold ${configTab === id ? "bg-gray-950 text-white" : "bg-gray-100 text-gray-700"}`}>
            {label}
          </button>
        ))}
      </div>

      <main className="min-h-0 flex-1 overflow-y-auto p-4">
        {configTab === "blocks" && (
          <div className="grid min-h-0 gap-4 lg:grid-cols-[350px_minmax(0,1fr)]">
            <aside className="rounded-2xl border bg-white p-4">
              <div>
                <h3 className="font-bold text-gray-950">Parts & Other</h3>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  Every block is assigned to one of the fixed Equipment lifecycle stages. Parts always gate lifecycle completion. Other blocks can either gate the selected stage or stay informational with Equipment status mapping = None.
                </p>
              </div>
              {!readOnly && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => addBlock("checklist")} className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-gray-950 px-3 py-2 text-xs font-bold text-white">
                    <Plus className="h-4 w-4" /> Add Part
                  </button>
                  <button type="button" onClick={() => addBlock("equipment_info")} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-orange-300 bg-orange-50 px-3 py-2 text-xs font-bold text-orange-800">
                    <Plus className="h-4 w-4" /> Add Other
                  </button>
                </div>
              )}
              <div className="mt-4 space-y-2">
                {sections.map((section, index) => {
                  const isOther = section.section_kind === "equipment_info";
                  const stage = stageById[section.stage_id];
                  const sectionColor = safeColor(legendById[section.legend_id]?.color || section.color || stage?.color || "#E5E7EB");
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setSelectedSectionId(section.id)}
                      className={`w-full overflow-hidden rounded-xl border text-left transition ${selectedSectionId === section.id ? "shadow-sm" : "hover:border-gray-300"}`}
                      style={layeredToneStyle(sectionColor, {
                        from: selectedSectionId === section.id ? 0.26 : 0.18,
                        to: 0.08,
                        borderAlpha: selectedSectionId === section.id ? 0.48 : 0.3,
                      })}
                    >
                      <div className="h-2" style={{ backgroundColor: sectionColor }} />
                      <div className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-semibold text-gray-900">{section.title}</div>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${isOther ? "bg-orange-50 text-orange-700" : "bg-gray-100 text-gray-600"}`}>
                            {isOther ? "Other" : "Part"}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {stage?.label || "Stage"} · {isOther ? (section.equipment_status_mapping ? "lifecycle-mapped" : "informational · not calculated") : "lifecycle gate"} · {section.questions?.length || 0} item{section.questions?.length === 1 ? "" : "s"}
                        </div>
                        <div className="mt-1 text-[10px] font-semibold text-gray-400">Order {index + 1}</div>
                      </div>
                    </button>
                  );
                })}
                {!sections.length && (
                  <div className="rounded-xl border border-dashed p-7 text-center text-xs leading-5 text-gray-500">
                    Start with <strong>Add Other</strong> for form information such as Specification # / Callout / Room, or <strong>Add Part</strong> for PART 1.
                  </div>
                )}
              </div>
            </aside>

            <section className="rounded-2xl border bg-white p-5">
              {!selectedSection ? (
                <div className="p-12 text-center text-sm text-gray-500">Select a block or add a Part / Other block.</div>
              ) : (
                <div className="space-y-5">
                  <div
                    className="rounded-2xl border p-4"
                    style={{
                      ...layeredToneStyle(legendById[selectedSection.legend_id]?.color || selectedSection.color || "#E5E7EB", { from: 0.22, to: 0.08, borderAlpha: 0.34 }),
                      borderTop: `8px solid ${legendById[selectedSection.legend_id]?.color || selectedSection.color || "#E5E7EB"}`,
                    }}
                  >
                    <div className="grid gap-4 lg:grid-cols-3">
                      <label className="text-sm font-semibold text-gray-700">
                        Block type
                        <select disabled={readOnly} value={selectedSection.section_kind || "checklist"} onChange={(event) => changeBlockKind(event.target.value)} className={`${inputClass} mt-1`}>
                          <option value="checklist">Part — lifecycle checklist</option>
                          <option value="equipment_info">Other — flexible block</option>
                        </select>
                      </label>
                      <label className="text-sm font-semibold text-gray-700">
                        {selectedIsOther ? "Other block title" : "Part title"}
                        <input disabled={readOnly} value={selectedSection.title || ""} onChange={(event) => patchSection(selectedSection.id, { title: event.target.value })} className={`${inputClass} mt-1`} />
                      </label>
                      <label className="text-sm font-semibold text-gray-700">
                        Lifecycle stage
                        <select
                          disabled={readOnly}
                          value={selectedSection.stage_id}
                          onChange={(event) => {
                            const stage = stageById[event.target.value];
                            const nextLifecycle = stage?.legacy_status || "incomplete";
                            patchSection(selectedSection.id, {
                              stage_id: event.target.value,
                              lifecycle_stage: nextLifecycle,
                              ...(selectedIsOther && selectedSection.equipment_status_mapping
                                ? { equipment_status_mapping: nextLifecycle }
                                : {}),
                            });
                          }}
                          className={`${inputClass} mt-1`}
                        >
                          {activeStages.map((stage) => <option key={stage.id} value={stage.id}>{stage.label}</option>)}
                        </select>
                      </label>
                      {selectedIsOther && (
                        <label className="text-sm font-semibold text-gray-700">
                          Equipment status mapping
                          <select
                            disabled={readOnly}
                            value={selectedSection.equipment_status_mapping || ""}
                            onChange={(event) => {
                              const value = event.target.value || null;
                              patchSection(selectedSection.id, {
                                equipment_status_mapping: value,
                                required: Boolean(value),
                                questions: (selectedSection.questions || []).map((question) => ({
                                  ...question,
                                  required: value ? question.required !== false : false,
                                  fields: (question.fields || []).map((field) => ({
                                    ...field,
                                    required: value ? field.required : false,
                                  })),
                                })),
                              });
                            }}
                            className={`${inputClass} mt-1`}
                          >
                            <option value="">None — informational only / not calculated</option>
                            <option value={selectedSection.lifecycle_stage || "incomplete"}>
                              {stageById[selectedSection.stage_id]?.label || "Selected lifecycle stage"}
                            </option>
                          </select>
                          <span className="mt-1 block text-[11px] font-normal leading-4 text-gray-500">
                            None keeps this block visible in its lifecycle stage but excludes it from lifecycle completion and readiness.
                          </span>
                        </label>
                      )}
                      <label className="text-sm font-semibold text-gray-700">
                        Color legend
                        <select disabled={readOnly} value={selectedSection.legend_id || ""} onChange={(event) => { const legend = legendById[event.target.value]; patchSection(selectedSection.id, { legend_id: event.target.value || null, color: legend?.color || selectedSection.color }); }} className={`${inputClass} mt-1`}>
                          <option value="">No legend assignment</option>
                          {(definition.color_legend || []).map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                        </select>
                      </label>
                      <label className="text-sm font-semibold text-gray-700 lg:col-span-2">
                        Description / instruction
                        <input disabled={readOnly} value={selectedSection.description || ""} onChange={(event) => patchSection(selectedSection.id, { description: event.target.value })} className={`${inputClass} mt-1`} placeholder={selectedIsOther ? "Optional note for this form block" : "Optional Part instruction"} />
                      </label>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                      <ColorPicker value={legendById[selectedSection.legend_id]?.color || selectedSection.color || "#E5E7EB"} disabled={readOnly || Boolean(selectedSection.legend_id)} onChange={(color) => patchSection(selectedSection.id, { color })} />
                      {!readOnly && (
                        <div className="flex gap-1">
                          <button type="button" disabled={sections.findIndex((item) => item.id === selectedSection.id) === 0} onClick={() => moveSection(-1)} className="rounded-lg border p-2 disabled:opacity-30" title="Move block up"><ArrowUp className="h-4 w-4" /></button>
                          <button type="button" disabled={sections.findIndex((item) => item.id === selectedSection.id) === sections.length - 1} onClick={() => moveSection(1)} className="rounded-lg border p-2 disabled:opacity-30" title="Move block down"><ArrowDown className="h-4 w-4" /></button>
                          <button type="button" onClick={() => removeSection(selectedSection.id)} className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700"><Trash2 className="h-4 w-4" /> Delete {selectedIsOther ? "Other" : "Part"}</button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-gray-950">{selectedIsOther ? "Items / fields" : "Questions"}</h3>
                      <p className="text-xs text-gray-500">
                        Each item can contain multiple controls, for example Dropdown + Comment + Document Upload. Dropdown/radio options are entered by the designer.
                      </p>
                    </div>
                    {!readOnly && (
                      <button type="button" onClick={addQuestion} className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-3 py-2 text-sm font-semibold text-white">
                        <Plus className="h-4 w-4" /> Add {selectedIsOther ? "item" : "question"}
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    {(selectedSection.questions || []).map((question, questionIndex) => (
                      <article
                        key={question.id}
                        className="overflow-hidden rounded-2xl border shadow-sm"
                        style={{
                          ...layeredToneStyle(legendById[question.legend_id]?.color || question.color || legendById[selectedSection.legend_id]?.color || selectedSection.color || "#E5E7EB", { from: 0.18, to: 0.06, borderAlpha: 0.28 }),
                          borderTop: `6px solid ${legendById[question.legend_id]?.color || question.color || legendById[selectedSection.legend_id]?.color || selectedSection.color || "#E5E7EB"}`,
                        }}
                      >
                        <div className="p-4">
                          <div className="flex items-start gap-3">
                            <span className="mt-2 min-w-7 text-center text-xs font-black text-gray-400">{questionIndex + 1}</span>
                            <div className="min-w-0 flex-1 space-y-3">
                              <textarea disabled={readOnly} rows={2} value={question.label || ""} onChange={(event) => patchQuestion(selectedSection.id, question.id, { label: event.target.value })} className={`${inputClass} font-semibold`} />
                              <div className="grid gap-3 md:grid-cols-3">
                                <label className="text-xs font-semibold text-gray-600">
                                  Responsible group
                                  <select disabled={readOnly} value={question.responsibility_group || "any"} onChange={(event) => patchQuestion(selectedSection.id, question.id, { responsibility_group: event.target.value })} className={`${inputClass} mt-1`}>
                                    {RESPONSIBILITY_GROUPS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                                  </select>
                                </label>
                                <label className="text-xs font-semibold text-gray-600">
                                  Color legend
                                  <select disabled={readOnly} value={question.legend_id || ""} onChange={(event) => { const legend = legendById[event.target.value]; patchQuestion(selectedSection.id, question.id, { legend_id: event.target.value || null, color: legend?.color || question.color }); }} className={`${inputClass} mt-1`}>
                                    <option value="">Use block color</option>
                                    {(definition.color_legend || []).map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                                  </select>
                                </label>
                                <label className="flex items-center gap-2 pt-6 text-sm font-semibold text-gray-700">
                                  <input
                                    disabled={readOnly || (selectedIsOther && !selectedSection.equipment_status_mapping)}
                                    type="checkbox"
                                    checked={selectedIsOther && !selectedSection.equipment_status_mapping ? false : question.required !== false}
                                    onChange={(event) => patchQuestion(selectedSection.id, question.id, { required: event.target.checked })}
                                  />
                                  {selectedIsOther ? "Required for lifecycle completion" : "Required for stage"}
                                </label>
                              </div>
                              <input disabled={readOnly} value={question.help_text || ""} onChange={(event) => patchQuestion(selectedSection.id, question.id, { help_text: event.target.value })} className={inputClass} placeholder="Optional instruction / acceptance note" />
                              <ColorPicker value={legendById[question.legend_id]?.color || question.color || legendById[selectedSection.legend_id]?.color || selectedSection.color || "#E5E7EB"} disabled={readOnly || Boolean(question.legend_id)} onChange={(color) => patchQuestion(selectedSection.id, question.id, { color })} />
                            </div>
                            {!readOnly && (
                              <div className="flex flex-col gap-1">
                                <button type="button" disabled={questionIndex === 0} onClick={() => patchSection(selectedSection.id, { questions: moveItem(selectedSection.questions || [], question.id, -1) })} className="rounded-lg border p-2 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                                <button type="button" disabled={questionIndex === (selectedSection.questions || []).length - 1} onClick={() => patchSection(selectedSection.id, { questions: moveItem(selectedSection.questions || [], question.id, 1) })} className="rounded-lg border p-2 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                                <button type="button" onClick={() => removeQuestion(question.id)} className="rounded-lg border border-red-200 p-2 text-red-700"><Trash2 className="h-4 w-4" /></button>
                              </div>
                            )}
                          </div>

                          <div className="mt-4 rounded-xl bg-gray-50 p-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <div className="text-xs font-black uppercase tracking-wide text-gray-500">Response fields</div>
                                <div className="text-xs text-gray-500">Examples: custom dropdown + comment, tick box + attachment, auto-filled Equipment field.</div>
                              </div>
                              {!readOnly && (
                                <div className="flex flex-wrap gap-1">
                                  <button type="button" onClick={() => addField(question.id, "select")} className="rounded-lg border bg-white px-2 py-1.5 text-xs font-semibold">+ Dropdown</button>
                                  <button type="button" onClick={() => addField(question.id, "radio")} className="rounded-lg border bg-white px-2 py-1.5 text-xs font-semibold">+ Radio</button>
                                  <button type="button" onClick={() => addField(question.id, "textarea")} className="rounded-lg border bg-white px-2 py-1.5 text-xs font-semibold">+ Comment</button>
                                  <button type="button" onClick={() => addField(question.id, "attachment")} className="rounded-lg border bg-white px-2 py-1.5 text-xs font-semibold">+ Document Upload</button>
                                  <button type="button" onClick={() => addField(question.id, "checkbox")} className="rounded-lg border bg-white px-2 py-1.5 text-xs font-semibold">+ Tick box</button>
                                  <button type="button" onClick={() => addField(question.id, "text")} className="rounded-lg border bg-white px-2 py-1.5 text-xs font-semibold">+ Text</button>
                                  <button type="button" onClick={() => addField(question.id, "number")} className="rounded-lg border bg-white px-2 py-1.5 text-xs font-semibold">+ Number</button>
                                  <button type="button" onClick={() => addField(question.id, "equipment_field")} className="rounded-lg border bg-white px-2 py-1.5 text-xs font-semibold">+ Equipment field</button>
                                </div>
                              )}
                            </div>

                            <div className="mt-3 space-y-3">
                              {(question.fields || []).map((field, fieldIndex) => (
                                <div key={field.id} className="rounded-xl border bg-white p-3">
                                  <div className="grid gap-3 lg:grid-cols-[200px_minmax(180px,1fr)_auto] lg:items-end">
                                    <label className="text-xs font-semibold text-gray-600">
                                      Field type
                                      <select disabled={readOnly} value={field.type} onChange={(event) => patchField(selectedSection.id, question.id, field.id, { type: event.target.value, options: ["select", "radio"].includes(event.target.value) ? field.options || [] : [], source_key: event.target.value === "equipment_field" ? (field.source_key || "equipment_tag") : null, required: event.target.value === "equipment_field" ? false : field.required })} className={`${inputClass} mt-1`}>
                                        {FIELD_TYPES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                                      </select>
                                    </label>
                                    <label className="text-xs font-semibold text-gray-600">
                                      Field label
                                      <input disabled={readOnly} value={field.label || ""} onChange={(event) => patchField(selectedSection.id, question.id, field.id, { label: event.target.value })} className={`${inputClass} mt-1`} />
                                    </label>
                                    {!readOnly && (
                                      <div className="flex gap-1">
                                        <button type="button" disabled={fieldIndex === 0} onClick={() => patchQuestion(selectedSection.id, question.id, { fields: moveItem(question.fields || [], field.id, -1) })} className="rounded-lg border p-2 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                                        <button type="button" disabled={fieldIndex === (question.fields || []).length - 1} onClick={() => patchQuestion(selectedSection.id, question.id, { fields: moveItem(question.fields || [], field.id, 1) })} className="rounded-lg border p-2 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                                        <button type="button" onClick={() => removeField(question.id, field.id)} className="rounded-lg border border-red-200 p-2 text-red-700"><Trash2 className="h-4 w-4" /></button>
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                                      <input disabled={readOnly || field.type === "equipment_field" || (selectedIsOther && !selectedSection.equipment_status_mapping)} type="checkbox" checked={selectedIsOther && !selectedSection.equipment_status_mapping ? false : !!field.required} onChange={(event) => patchField(selectedSection.id, question.id, field.id, { required: event.target.checked })} /> Required for completion
                                    </label>
                                    {field.type === "attachment" && (
                                      <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
                                        Uploading or linking a document completes this field. The uploader can remove it if the wrong file was added.
                                      </div>
                                    )}
                                  </div>
                                  {["select", "radio"].includes(field.type) && (
                                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                                      <label className="text-xs font-semibold text-gray-600">
                                        Designer-defined options — comma separated
                                        <input disabled={readOnly} value={(field.options || []).join(", ")} onChange={(event) => patchField(selectedSection.id, question.id, field.id, { options: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) })} className={`${inputClass} mt-1`} placeholder="Approved, Approved with Comments, Rejected, N/A" />
                                      </label>
                                      <label className="text-xs font-semibold text-gray-600">
                                        Failure values — optional
                                        <input disabled={readOnly || (selectedIsOther && !selectedSection.equipment_status_mapping)} value={(field.failure_values || []).join(", ")} onChange={(event) => patchField(selectedSection.id, question.id, field.id, { failure_values: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) })} className={`${inputClass} mt-1`} placeholder="Rejected, No" />
                                      </label>
                                    </div>
                                  )}
                                  {field.type === "equipment_field" && (
                                    <label className="mt-3 block text-xs font-semibold text-gray-600">
                                      Equipment source
                                      <select disabled={readOnly} value={field.source_key || "equipment_tag"} onChange={(event) => patchField(selectedSection.id, question.id, field.id, { source_key: event.target.value, required: false })} className={`${inputClass} mt-1`}>
                                        {EQUIPMENT_FIELDS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                                      </select>
                                    </label>
                                  )}
                                  {["text", "textarea", "number"].includes(field.type) && (
                                    <label className="mt-3 block text-xs font-semibold text-gray-600">
                                      Placeholder
                                      <input disabled={readOnly} value={field.placeholder || ""} onChange={(event) => patchField(selectedSection.id, question.id, field.id, { placeholder: event.target.value })} className={`${inputClass} mt-1`} />
                                    </label>
                                  )}
                                </div>
                              ))}
                              {!question.fields?.length && <div className="rounded-lg border border-dashed p-5 text-center text-xs text-gray-500">Add at least one response field before publishing.</div>}
                            </div>
                          </div>
                        </div>
                      </article>
                    ))}
                    {!selectedSection.questions?.length && <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-gray-500">No items in this block yet.</div>}
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {configTab === "legend" && (
          <section className="mx-auto max-w-5xl rounded-2xl border bg-white p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2"><Palette className="h-5 w-5 text-orange-600" /><h3 className="font-bold text-gray-950">Color Legend</h3></div>
                <p className="mt-1 text-sm text-gray-500">Legend entries are displayed on the live checklist and retained for the future PDF report.</p>
              </div>
              {!readOnly && <button type="button" onClick={addLegend} className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-3 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add legend</button>}
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm font-semibold text-gray-700"><input type="checkbox" disabled={readOnly} checked={definition.report_layout?.show_color_legend !== false} onChange={(event) => setDefinition((current) => ({ ...current, report_layout: { ...(current.report_layout || {}), show_color_legend: event.target.checked } }))} /> Display this legend on the checklist and final report</label>
            <div className="mt-5 space-y-3">
              {(definition.color_legend || []).map((item, index) => (
                <div key={item.id} className="rounded-xl border p-4" style={{ borderLeft: `8px solid ${item.color}` }}>
                  <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)_auto] lg:items-end">
                    <label className="text-xs font-semibold text-gray-600">Label<input disabled={readOnly} value={item.label} onChange={(event) => setDefinition((current) => ({ ...current, color_legend: current.color_legend.map((entry) => entry.id === item.id ? { ...entry, label: event.target.value } : entry) }))} className={`${inputClass} mt-1`} /></label>
                    <label className="text-xs font-semibold text-gray-600">Description<input disabled={readOnly} value={item.description || ""} onChange={(event) => setDefinition((current) => ({ ...current, color_legend: current.color_legend.map((entry) => entry.id === item.id ? { ...entry, description: event.target.value } : entry) }))} className={`${inputClass} mt-1`} /></label>
                    {!readOnly && <div className="flex gap-1"><button type="button" disabled={index === 0} onClick={() => setDefinition((current) => ({ ...current, color_legend: moveItem(current.color_legend, item.id, -1) }))} className="rounded-lg border p-2 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button><button type="button" disabled={index === definition.color_legend.length - 1} onClick={() => setDefinition((current) => ({ ...current, color_legend: moveItem(current.color_legend, item.id, 1) }))} className="rounded-lg border p-2 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button><button type="button" onClick={() => removeLegend(item.id)} className="rounded-lg border border-red-200 p-2 text-red-700"><Trash2 className="h-4 w-4" /></button></div>}
                  </div>
                  <div className="mt-3"><ColorPicker value={item.color} disabled={readOnly} onChange={(color) => setDefinition((current) => ({ ...current, color_legend: current.color_legend.map((entry) => entry.id === item.id ? { ...entry, color } : entry) }))} /></div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
export default function ChecklistTemplatesView({ projectId, options, canManage }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editorVersionId, setEditorVersionId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [renameSource, setRenameSource] = useState(null);
  const [renameName, setRenameName] = useState("");
  const [renameClassificationId, setRenameClassificationId] = useState("");
  const [renameTypeId, setRenameTypeId] = useState("");
  const [renameDefaultForType, setRenameDefaultForType] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [classificationFilter, setClassificationFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [cloneForm, setCloneForm] = useState({
    name: "",
    classification_id: "",
    type_id: "",
    is_default_for_type: false,
    description: "",
  });
  const [form, setForm] = useState({
    name: "ATS Pre-Functional Checklist",
    classification_id: "",
    type_id: "",
    is_default_for_type: false,
    description: "Flexible staged Equipment checklist.",
  });

  const classifications = options?.classifications || options?.types || [];
  const specificTypes = options?.specific_types || [];
  const typesForClassification = (classificationId) =>
    specificTypes.filter((item) =>
      !classificationId || Number(item.classification_id) === Number(classificationId),
    );

  const visibleTemplates = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return templates.filter((template) => {
      if (classificationFilter !== "all" && String(template.classification_id || "none") !== classificationFilter) return false;
      if (typeFilter !== "all" && String(template.type_id || "none") !== typeFilter) return false;
      if (!needle) return true;
      return [template.name, template.description, template.classification_name, template.type_name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [templates, search, classificationFilter, typeFilter]);

  const templateGroups = useMemo(() => {
    const grouped = new Map();
    visibleTemplates.forEach((template) => {
      const classificationKey = template.classification_name || "Generic / All Classifications";
      const typeKey = template.type_name || (template.classification_name ? "Classification-wide" : "Generic");
      if (!grouped.has(classificationKey)) grouped.set(classificationKey, new Map());
      const typeMap = grouped.get(classificationKey);
      if (!typeMap.has(typeKey)) typeMap.set(typeKey, []);
      typeMap.get(typeKey).push(template);
    });
    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([classification, typeMap]) => ({
        classification,
        types: Array.from(typeMap.entries())
          .sort(([a], [b]) => {
            if (a === "Classification-wide" || a === "Generic") return -1;
            if (b === "Classification-wide" || b === "Generic") return 1;
            return a.localeCompare(b);
          })
          .map(([type, items]) => ({ type, items })),
      }));
  }, [visibleTemplates]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const response = await equipmentAPI.listChecklistTemplates(projectId, { include_inactive: showArchived });
      setTemplates(response.data || []);
    } catch (err) {
      setError(apiMessage(err, "Failed to load checklist templates."));
    } finally {
      setLoading(false);
    }
  }, [projectId, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    setError("");
    try {
      const response = await equipmentAPI.createChecklistTemplate(projectId, {
        name: form.name.trim(),
        classification_id: form.classification_id ? Number(form.classification_id) : null,
        type_id: form.type_id ? Number(form.type_id) : null,
        is_default_for_type: Boolean(form.type_id && form.is_default_for_type),
        description: form.description || null,
        definition: defaultDefinition(form.name.trim()),
      });
      setCreateOpen(false);
      await load();
      const versionId = response.data?.draft_version?.id || response.data?.versions?.find((item) => item.status === "draft")?.id;
      if (versionId) setEditorVersionId(versionId);
    } catch (err) {
      setError(apiMessage(err, "Failed to create checklist template."));
    } finally {
      setLoading(false);
    }
  };

  const openTemplate = async (template) => {
    if (template.is_active === false) {
      if (template.published_version?.id) setEditorVersionId(template.published_version.id);
      return;
    }
    if (template.draft_version?.id) {
      setEditorVersionId(template.draft_version.id);
      return;
    }
    if (!canManage) {
      if (template.published_version?.id) setEditorVersionId(template.published_version.id);
      return;
    }
    try {
      const response = await equipmentAPI.createChecklistTemplateVersion(projectId, template.id, {
        from_version_id: template.published_version?.id || null,
        change_note: "New flexible checklist revision",
      });
      setEditorVersionId(response.data.id);
      await load();
    } catch (err) {
      setError(apiMessage(err, "Failed to create a new checklist revision."));
    }
  };

  const openCloneTemplate = (template) => {
    setCloneSource(template);
    setCloneForm({
      name: `${template.name} Copy`,
      // Classification / Type intentionally do not carry over from the source.
      classification_id: "",
      type_id: "",
      is_default_for_type: false,
      description: template.description || "",
    });
  };

  const cloneTemplate = async () => {
    if (!cloneSource || !cloneForm.name.trim()) return;
    setLoading(true);
    setError("");
    try {
      const response = await equipmentAPI.cloneChecklistTemplate(projectId, cloneSource.id, {
        name: cloneForm.name.trim(),
        classification_id: cloneForm.classification_id ? Number(cloneForm.classification_id) : null,
        type_id: cloneForm.type_id ? Number(cloneForm.type_id) : null,
        is_default_for_type: Boolean(cloneForm.type_id && cloneForm.is_default_for_type),
        description: cloneForm.description || null,
        from_version_id:
          cloneSource.published_version?.id || cloneSource.draft_version?.id || null,
      });
      setCloneSource(null);
      await load();
      const versionId =
        response.data?.draft_version?.id ||
        response.data?.versions?.find((item) => item.status === "draft")?.id;
      if (versionId) setEditorVersionId(versionId);
    } catch (err) {
      setError(apiMessage(err, "Failed to clone checklist template."));
    } finally {
      setLoading(false);
    }
  };

  const openRenameTemplate = (template) => {
    setRenameSource(template);
    setRenameName(template.name || "");
    setRenameClassificationId(template.classification_id ? String(template.classification_id) : "");
    setRenameTypeId(template.type_id ? String(template.type_id) : "");
    setRenameDefaultForType(Boolean(template.is_default_for_type));
    setError("");
  };

  const renameTemplate = async () => {
    const cleanName = renameName.trim();
    if (!renameSource || !cleanName) return;
    setRenaming(true);
    setError("");
    try {
      await equipmentAPI.updateChecklistTemplate(projectId, renameSource.id, {
        name: cleanName,
        classification_id: renameClassificationId ? Number(renameClassificationId) : null,
        clear_equipment_type: !renameClassificationId,
        type_id: renameTypeId ? Number(renameTypeId) : null,
        clear_type: !renameTypeId,
        is_default_for_type: Boolean(renameTypeId && renameDefaultForType),
      });
      setRenameSource(null);
      setRenameName("");
      setRenameClassificationId("");
      setRenameTypeId("");
      setRenameDefaultForType(false);
      await load();
    } catch (err) {
      setError(apiMessage(err, "Failed to update checklist template settings."));
    } finally {
      setRenaming(false);
    }
  };

  const deleteTemplate = async (template) => {
    const active = Number(template.active_assignment_count ?? template.usage_count ?? 0);
    const historical = Number(template.historical_usage_count || 0);
    if (active > 0) {
      const names = (template.active_equipment || [])
        .map((item) => item.equipment_number || item.equipment_tag)
        .filter(Boolean)
        .join(", ");
      setError(
        `This template is still assigned to ${active} equipment record${active === 1 ? "" : "s"}${names ? ` (${names}${active > (template.active_equipment || []).length ? ", …" : ""})` : ""}. Unlink the assigned checklist first.`,
      );
      return;
    }
    const warning = historical > 0
      ? `Remove this template from the active template library? Historical checklist execution for ${historical} equipment record${historical === 1 ? "" : "s"} will be preserved.`
      : "Delete this checklist template permanently? This cannot be undone.";
    if (!window.confirm(warning)) return;
    try {
      await equipmentAPI.deleteChecklistTemplate(projectId, template.id);
      await load();
    } catch (err) {
      setError(apiMessage(err, "Failed to delete checklist template."));
    }
  };

  const renderTemplateCard = (template) => (
    <div key={template.id} className={`rounded-2xl border bg-white p-5 transition hover:shadow-sm ${template.is_active === false ? "border-gray-200 opacity-70" : "border-gray-200 hover:border-gray-300"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-bold text-gray-950">{template.name}</h3>
          <p className="mt-1 text-xs font-medium text-gray-600">
            {template.classification_name || "Generic classification"}
            <span className="mx-1.5 text-gray-300">→</span>
            {template.type_name || (template.classification_name ? "Classification-wide" : "Generic")}
          </p>
        </div>
        <LayoutTemplate className="h-5 w-5 text-gray-400" />
      </div>
      <p className="mt-3 min-h-10 text-sm text-gray-600">{template.description || "Reusable Equipment questionnaire."}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {template.published_version && <StatusChip status="published" />}
        {template.draft_version && <StatusChip status="draft" />}
        {template.is_default_for_type && <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-800">Default for Type</span>}
        {template.is_active === false && <span className="rounded-full bg-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700">Archived</span>}
        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">{template.active_assignment_count ?? template.usage_count ?? 0} assigned</span>
        {Number(template.historical_usage_count || 0) > 0 && <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600">{template.historical_usage_count} historical</span>}
      </div>
      {(template.active_equipment || []).length > 0 && <p className="mt-2 truncate text-xs text-gray-500" title={(template.active_equipment || []).map((item) => `${item.equipment_number || "Equipment"}${item.equipment_tag ? ` · ${item.equipment_tag}` : ""}`).join(", ")}>Assigned: {(template.active_equipment || []).map((item) => item.equipment_number || item.equipment_tag).filter(Boolean).join(", ")}{Number(template.active_assignment_count || 0) > (template.active_equipment || []).length ? ", …" : ""}</p>}
      {Number(template.active_assignment_count ?? template.usage_count ?? 0) === 0 && (template.historical_equipment || []).length > 0 && <p className="mt-2 truncate text-xs text-gray-400" title={(template.historical_equipment || []).map((item) => `${item.equipment_number || "Equipment"}${item.equipment_tag ? ` · ${item.equipment_tag}` : ""}`).join(", ")}>History: {(template.historical_equipment || []).map((item) => item.equipment_number || item.equipment_tag).filter(Boolean).join(", ")}{Number(template.historical_usage_count || 0) > (template.historical_equipment || []).length ? ", …" : ""}</p>}
      <div className="mt-5 flex gap-2">
        <button type="button" onClick={() => openTemplate(template)} className="flex-1 rounded-xl border px-3 py-2 text-sm font-semibold">{template.is_active === false ? "View archived" : template.draft_version ? "Continue design" : canManage ? "Edit / new revision" : "View template"}</button>
        {canManage && <button type="button" onClick={() => openRenameTemplate(template)} className="rounded-xl border p-2.5 text-gray-700" title="Edit template settings"><Pencil className="h-4 w-4" /></button>}
        {canManage && <button type="button" onClick={() => openCloneTemplate(template)} className="rounded-xl border p-2.5" title="Clone template"><Copy className="h-4 w-4" /></button>}
        {canManage && template.is_active !== false && <button type="button" onClick={() => deleteTemplate(template)} disabled={Number(template.active_assignment_count ?? template.usage_count ?? 0) > 0} className="rounded-xl border border-red-200 p-2.5 text-red-700 disabled:cursor-not-allowed disabled:opacity-35" title={Number(template.active_assignment_count ?? template.usage_count ?? 0) > 0 ? "Unlink this template from assigned equipment before deleting" : "Delete template"}><Trash2 className="h-4 w-4" /></button>}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
        <div><div className="flex items-center gap-2"><LayoutTemplate className="h-5 w-5 text-orange-600" /><h2 className="text-lg font-bold text-gray-950">Equipment Checklist Templates</h2></div><p className="mt-1 text-sm text-gray-500">Design the PFC as one ordered form using Parts and flexible Other blocks. Response controls, dropdown choices, stages and colors are defined by the designer—not hard-coded.</p></div>
        {canManage && <div className="flex flex-wrap items-center gap-2"><button type="button" onClick={() => setShowArchived((current) => !current)} className={`rounded-xl border px-3 py-2.5 text-sm font-semibold ${showArchived ? "border-gray-400 bg-gray-100 text-gray-900" : "border-gray-200 bg-white text-gray-600"}`}>{showArchived ? "Hide archived" : "Show archived"}</button><button type="button" onClick={() => setCreateOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> New template</button></div>}
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_240px_240px]">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search template, Classification or Type..."
            className={inputClass}
          />
          <select
            value={classificationFilter}
            onChange={(event) => {
              setClassificationFilter(event.target.value);
              setTypeFilter("all");
            }}
            className={inputClass}
          >
            <option value="all">All classifications</option>
            <option value="none">Generic / no classification</option>
            {classifications.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className={inputClass}>
            <option value="all">All types</option>
            <option value="none">Generic / classification-wide</option>
            {(classificationFilter === "none" ? [] : typesForClassification(classificationFilter === "all" ? null : classificationFilter)).map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>
        <p className="mt-2 text-xs text-gray-500">Templates are organized as Classification → Type. A Type-specific published template can be marked as the automatic default.</p>
      </div>

      {loading && !templates.length ? (
        <div className="flex justify-center p-16"><Loader2 className="h-7 w-7 animate-spin text-orange-500" /></div>
      ) : templateGroups.length ? (
        <div className="space-y-6">
          {templateGroups.map((group) => (
            <section key={group.classification} className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
              <div className="mb-4 flex items-center justify-between gap-3 border-b border-gray-200 pb-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Classification</div>
                  <h3 className="mt-0.5 text-lg font-bold text-gray-950">{group.classification}</h3>
                </div>
                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-600 ring-1 ring-gray-200">
                  {group.types.reduce((total, typeGroup) => total + typeGroup.items.length, 0)} templates
                </span>
              </div>
              <div className="space-y-5">
                {group.types.map((typeGroup) => (
                  <div key={`${group.classification}-${typeGroup.type}`}>
                    <div className="mb-2 flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-800">Type: {typeGroup.type}</span>
                      <span className="text-xs text-gray-400">{typeGroup.items.length}</span>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {typeGroup.items.map(renderTemplateCard)}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-14 text-center">
          <LayoutTemplate className="mx-auto h-10 w-10 text-gray-300" />
          <h3 className="mt-3 font-bold text-gray-900">{templates.length ? "No templates match these filters" : "No Equipment checklist templates yet"}</h3>
          <p className="mt-1 text-sm text-gray-500">{templates.length ? "Adjust the search, Classification or Type filter." : "Create a template, assign its Classification and Type, then build the staged checklist."}</p>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-gray-950/50 p-4">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">New Equipment checklist</h3>
                <p className="text-sm text-gray-500">Target a Classification and, when needed, a specific Type.</p>
              </div>
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-lg p-2"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <label className="block text-sm font-medium">Template name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className={`${inputClass} mt-1`} /></label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium">
                  Classification
                  <select value={form.classification_id} onChange={(event) => setForm({ ...form, classification_id: event.target.value, type_id: "", is_default_for_type: false })} className={`${inputClass} mt-1`}>
                    <option value="">Generic / all classifications</option>
                    {classifications.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-medium">
                  Type
                  <select value={form.type_id} disabled={!form.classification_id} onChange={(event) => setForm({ ...form, type_id: event.target.value, is_default_for_type: event.target.value ? form.is_default_for_type : false })} className={`${inputClass} mt-1`}>
                    <option value="">Classification-wide / generic</option>
                    {typesForClassification(form.classification_id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>
              </div>
              <label className={`flex items-start gap-3 rounded-xl border p-3 ${form.type_id ? "border-indigo-200 bg-indigo-50" : "border-gray-200 bg-gray-50"}`}>
                <input type="checkbox" checked={form.is_default_for_type} disabled={!form.type_id} onChange={(event) => setForm({ ...form, is_default_for_type: event.target.checked })} className="mt-0.5 h-4 w-4 rounded" />
                <span><span className="block text-sm font-semibold text-gray-800">Default checklist for this Type</span><span className="block text-xs text-gray-500">Once a version is published, new equipment with this Type will receive it automatically.</span></span>
              </label>
              <label className="block text-sm font-medium">Description<textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} rows={3} className={`${inputClass} mt-1`} /></label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-xl border px-4 py-2 text-sm font-semibold">Cancel</button>
              <button type="button" onClick={create} disabled={!form.name.trim() || loading} className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create template</button>
            </div>
          </div>
        </div>
      )}

      {renameSource && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-gray-950/50 p-4">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold">Template settings</h3>
                <p className="text-sm text-gray-500">Rename or retarget the reusable template. Published versions and checklist execution history stay intact.</p>
              </div>
              <button type="button" onClick={() => setRenameSource(null)} disabled={renaming} className="rounded-lg p-2 disabled:opacity-50"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <label className="block text-sm font-medium">Template name<input autoFocus value={renameName} onChange={(event) => setRenameName(event.target.value)} className={`${inputClass} mt-1`} /></label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium">
                  Classification
                  <select value={renameClassificationId} onChange={(event) => { setRenameClassificationId(event.target.value); setRenameTypeId(""); setRenameDefaultForType(false); }} className={`${inputClass} mt-1`}>
                    <option value="">Generic / all classifications</option>
                    {classifications.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-medium">
                  Type
                  <select value={renameTypeId} disabled={!renameClassificationId} onChange={(event) => { setRenameTypeId(event.target.value); if (!event.target.value) setRenameDefaultForType(false); }} className={`${inputClass} mt-1`}>
                    <option value="">Classification-wide / generic</option>
                    {typesForClassification(renameClassificationId).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>
              </div>
              <label className={`flex items-start gap-3 rounded-xl border p-3 ${renameTypeId ? "border-indigo-200 bg-indigo-50" : "border-gray-200 bg-gray-50"}`}>
                <input type="checkbox" checked={renameDefaultForType} disabled={!renameTypeId} onChange={(event) => setRenameDefaultForType(event.target.checked)} className="mt-0.5 h-4 w-4 rounded" />
                <span><span className="block text-sm font-semibold text-gray-800">Default checklist for this Type</span><span className="block text-xs text-gray-500">Only one template can be the automatic default for each Type.</span></span>
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setRenameSource(null)} disabled={renaming} className="rounded-xl border px-4 py-2 text-sm font-semibold disabled:opacity-50">Cancel</button>
              <button type="button" onClick={renameTemplate} disabled={renaming || !renameName.trim()} className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{renaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />} Save settings</button>
            </div>
          </div>
        </div>
      )}

      {cloneSource && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-gray-950/50 p-4">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold">Clone Equipment checklist</h3>
                <p className="text-sm text-gray-500">Checklist content is copied; Classification, Type and default assignment are chosen independently.</p>
              </div>
              <button type="button" onClick={() => setCloneSource(null)} disabled={loading} className="rounded-lg p-2 disabled:opacity-50"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <label className="block text-sm font-medium">Template name<input value={cloneForm.name} onChange={(event) => setCloneForm((current) => ({ ...current, name: event.target.value }))} className={`${inputClass} mt-1`} /></label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium">
                  Classification
                  <select value={cloneForm.classification_id} onChange={(event) => setCloneForm((current) => ({ ...current, classification_id: event.target.value, type_id: "", is_default_for_type: false }))} className={`${inputClass} mt-1`}>
                    <option value="">Generic / all classifications</option>
                    {classifications.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-medium">
                  Type
                  <select value={cloneForm.type_id} disabled={!cloneForm.classification_id} onChange={(event) => setCloneForm((current) => ({ ...current, type_id: event.target.value, is_default_for_type: event.target.value ? current.is_default_for_type : false }))} className={`${inputClass} mt-1`}>
                    <option value="">Classification-wide / generic</option>
                    {typesForClassification(cloneForm.classification_id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>
              </div>
              <label className={`flex items-start gap-3 rounded-xl border p-3 ${cloneForm.type_id ? "border-indigo-200 bg-indigo-50" : "border-gray-200 bg-gray-50"}`}>
                <input type="checkbox" checked={cloneForm.is_default_for_type} disabled={!cloneForm.type_id} onChange={(event) => setCloneForm((current) => ({ ...current, is_default_for_type: event.target.checked }))} className="mt-0.5 h-4 w-4 rounded" />
                <span><span className="block text-sm font-semibold text-gray-800">Default checklist for this Type</span><span className="block text-xs text-gray-500">The source template default flag is not copied automatically.</span></span>
              </label>
              <label className="block text-sm font-medium">Description<textarea value={cloneForm.description} onChange={(event) => setCloneForm((current) => ({ ...current, description: event.target.value }))} rows={3} className={`${inputClass} mt-1`} /></label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setCloneSource(null)} disabled={loading} className="rounded-xl border px-4 py-2 text-sm font-semibold disabled:opacity-50">Cancel</button>
              <button type="button" onClick={cloneTemplate} disabled={!cloneForm.name.trim() || loading} className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />} Clone template</button>
            </div>
          </div>
        </div>
      )}

      {editorVersionId && <StructuredTemplateEditor projectId={projectId} versionId={editorVersionId} onClose={() => setEditorVersionId(null)} onSaved={load} />}
    </div>
  );
}
